'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

import Container from '@/layouts/Container';
import { ArrowIcon } from '@/icons';
import Svg from '../Svg';
import { isWebview } from '@/utils/isWebview';

/**
 * Takes an URL String and removes query params and hash params
 *
 * @param url - The URL string
 * @returns The transformed URL string
 *
 */
const getPathFromUrl = (url: string): string => {
  return url.split(/[?#]/)[0];
};

/**
 * Takes a breadcrumb title (from url path) and replaces
 * special chars to more readable chars
 *
 * @param title - The breadcrumb title
 * @returns The transformed title or the result of the custom transformLabel function
 *
 */
const convertBreadcrumb = (
  title: string,
  toUpperCase: boolean | undefined,
  toCapsLock: boolean | undefined,
  replaceCharacterList: Array<CharacterMap> | undefined,
  transformLabel?: ((title: string) => React.ReactNode) | undefined
): React.ReactNode => {
  let transformedTitle = getPathFromUrl(title);

  if (transformLabel) {
    return transformLabel(transformedTitle);
  }

  if (replaceCharacterList) {
    for (let i = 0; i < replaceCharacterList.length; i++) {
      transformedTitle = transformedTitle.replaceAll(
        replaceCharacterList[i].from,
        replaceCharacterList[i].to
      );
    }
  }

  // decode for utf-8 characters and return ascii.
  transformedTitle = toUpperCase
    ? decodeURI(transformedTitle).replace(/(\w)(\w*)/g, function (_, g1, g2) {
        return g1.toUpperCase() + g2.toLowerCase();
      })
    : decodeURI(transformedTitle);

  // decode for utf-8 characters and return ascii.
  return toCapsLock
    ? decodeURI(transformedTitle).toUpperCase()
    : decodeURI(transformedTitle);
};

export interface Breadcrumb {
  /** Breadcrumb title. Example: 'blog-entries' */
  breadcrumb: string;

  /** The URL which the breadcrumb points to. Example: 'blog/blog-entries' */
  href: string;
}

export interface CharacterMap {
  /** The source character or character pattern that should be replaced (e.g. 'ae') */
  from: string;

  /** The replacement into which the character should be replaced. */
  to: string;
}

type ClassNamesTypes = {
  /** Classes to be used for the outer container. */
  root?: string;
  rootContainer?: string;
  list?: {
    dot?: string;
    link?: string;
  };
};

export interface BreadcrumbsProps {
  /** If true, the default styles are used.
   * Make sure to import the CSS in _app.js
   * Example: true Default: false */
  useDefaultStyle?: boolean;

  /** Manually passed path for the breadcrumb */
  path?: string;

  /** The title for the very first breadcrumb pointing to the root directory. Example: '/' Default: 'HOME' */
  rootLabel?: string | null;

  /** Boolean indicator whether the root label should be omitted. Example: true Default: false */
  omitRootLabel?: boolean;

  /** Boolean indicator if the labels should be displayed as 1st letter upper case. Example: true Default: true */
  labelsToUppercase?: boolean | undefined;

  /** Boolean indicator if the labels should be displayed as All Caps. Example: true Default: false */
  labelsToCapsLock?: boolean | undefined;

  /** Array containing a list of specific characters that should be replaced in the label. This can be useful to convert special characters such as vowels. Example: [{ from: 'ae', to: 'ä' }, { from: '-', to: ' '}] Default: [{ from: '-', to: ' ' }] */
  replaceCharacterList?: Array<CharacterMap> | undefined;

  /** A transformation function that allows to customize the label strings. Receives the label string and has to return a string or React Component */
  transformLabel?: ((title: string) => React.ReactNode) | undefined;

  /** Array containing all the indexes of the path that should be omitted and not be rendered as labels. If we have a path like '/home/category/1' then you might want to pass '[2]' here, which omits the breadcrumb label '1'. Indexes start with 0. Example: [2] Default: undefined */
  omitIndexList?: Array<number> | undefined;

  /** Custom Classnames */
  classNames?: ClassNamesTypes;
}

/**
 * A functional React component for Next.js that renders a dynamic Breadcrumb navigation
 * based on the current path within the Next.js router navigation.
 *
 * Only works in conjunction with Next.js, since it leverages the Next.js router.
 *
 * By setting useDefaultStyle to true, the default CSS will be used.
 * The component is highly customizable by either custom classes or
 * inline styles, which can be passed as props.
 *
 * @param props - object of type BreadcrumbsProps
 * @returns The breadcrumb React component.
 */

const Breadcrumbs = ({
  // useDefaultStyle = false,
  path = '',
  rootLabel = 'Home',
  omitRootLabel = false,
  labelsToUppercase = true,
  labelsToCapsLock = false,
  replaceCharacterList = [
    { from: '-', to: ' ' },
    { from: '_', to: ' ' },
  ],
  transformLabel = undefined,
  omitIndexList = undefined,
  classNames,
}: BreadcrumbsProps) => {
  const router = usePathname();
  if (isWebview()) {
    return null;
  }
  const [breadcrumbs, setBreadcrumbs] = useState<Array<Breadcrumb> | null>(
    null
  );

  useEffect(() => {
    if (router) {
      /* if the path is passed manually use it
       * else if the page is product details(includes /shop/product/) then replace breadcrumb with /shop//
       * else use the router path as usual
       */
      const pathname = path
        ? path
        : router.includes('/shop/product/')
          ? '/shop//'
          : router;
      const linkPath = pathname.split('/');
      linkPath.shift();

      const pathArray = linkPath.map((path, i) => {
        return {
          breadcrumb: path,
          href: `/${
            // if the path index is 1 and path is product then replace the link with one link previous
            linkPath
              .slice(0, i === 1 && path === 'product' ? i : i + 1)
              .join('/')
          }`,
        };
      });
      setBreadcrumbs(pathArray);
    }
  }, [router]);

  const COMMON_CLASSNAMES = {
    dot: 'block h-1 w-1 rounded-full bg-black',
    list: {
      link: 'flex items-center gap-3 text-black hover:underline',
    },
  };

  if (!breadcrumbs) {
    return (
      <Container
        className={twMerge(
          'pt-2 flex gap-x-2 items-center justify-start',
          classNames?.rootContainer
        )}>
        <div className='loading-color-transition w-10 h-1 p-3 rounded-md lg:px-0' />
        <span
          className={twMerge(COMMON_CLASSNAMES?.dot, classNames?.list?.dot)}
        />
        <div className='loading-color-transition w-16 h-1 p-3 rounded-md lg:px-0' />
      </Container>
    );
  }

  return (
    <Container className={classNames?.rootContainer}>
      <nav
        className={twMerge(`px-3 py-4 lg:px-0`, classNames?.root)}
        aria-label='breadcrumbs'>
        <ol className={twMerge(`flex gap-3`)}>
          {!omitRootLabel && (
            <li className={twMerge(breadcrumbs.length >= 4 && 'max-md:hidden')}>
              <Link
                href='/'
                className={twMerge(
                  `text-black hover:underline`,
                  classNames?.list?.link
                )}>
                <span>
                  {convertBreadcrumb(
                    rootLabel || 'Home',
                    labelsToUppercase,
                    labelsToCapsLock,
                    replaceCharacterList,
                    transformLabel
                  )}
                </span>
              </Link>
            </li>
          )}
          {breadcrumbs.length >= 1 &&
            breadcrumbs.map((breadcrumb, i) => {
              if (
                !breadcrumb ||
                breadcrumb.breadcrumb.length === 0 ||
                (omitIndexList &&
                  (omitIndexList.find(value => value === i) === 0 ||
                    omitIndexList?.find(value => value === i)))
              ) {
                return;
              }

              return (
                <li
                  key={i}
                  className={twMerge(
                    breadcrumbs.length >= 4 && 'max-md:hidden'
                  )}>
                  {i === breadcrumbs.length - 2 ? (
                    <a
                      className={twMerge(
                        COMMON_CLASSNAMES?.list?.link,
                        classNames?.list?.link,
                        'hover:no-underline'
                      )}>
                      <span
                        className={twMerge(
                          COMMON_CLASSNAMES?.dot,
                          classNames?.list?.dot
                        )}
                      />
                      <span>
                        {convertBreadcrumb(
                          breadcrumb.breadcrumb,
                          labelsToUppercase,
                          labelsToCapsLock,
                          replaceCharacterList,
                          transformLabel
                        )}
                      </span>
                    </a>
                  ) : (
                    <Link
                      href={breadcrumb.href}
                      className={twMerge(
                        COMMON_CLASSNAMES?.list?.link,
                        classNames?.list?.link
                      )}>
                      {omitRootLabel ? (
                        i !== 0 && (
                          <span
                            className={twMerge(
                              COMMON_CLASSNAMES?.dot,
                              classNames?.list?.dot
                            )}
                          />
                        )
                      ) : (
                        <span
                          className={twMerge(
                            COMMON_CLASSNAMES?.dot,
                            classNames?.list?.dot
                          )}
                        />
                      )}
                      <span>
                        {convertBreadcrumb(
                          breadcrumb.breadcrumb,
                          labelsToUppercase,
                          labelsToCapsLock,
                          replaceCharacterList,
                          transformLabel
                        )}
                      </span>
                    </Link>
                  )}
                </li>
              );
            })}
          {breadcrumbs.length >= 4 && (
            <li className='md:hidden'>
              <Link
                href={breadcrumbs[breadcrumbs.length - 3]?.href}
                className={twMerge(
                  COMMON_CLASSNAMES?.list?.link,
                  classNames?.list?.link,
                  'flex flex-row items-center justify-center gap-x-1'
                )}>
                <Svg
                  className='h-4 w-4 rotate-90 stroke-fig'
                  viewBox='0 0 14 6'>
                  <ArrowIcon />
                </Svg>
                {convertBreadcrumb(
                  breadcrumbs[breadcrumbs.length - 3]?.breadcrumb,
                  labelsToUppercase,
                  labelsToCapsLock,
                  replaceCharacterList,
                  transformLabel
                )}
              </Link>
            </li>
          )}
        </ol>
      </nav>
    </Container>
  );
};

export default Breadcrumbs;
